import { useState } from "react";
import { FolderOpen } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import StrategyRoomCTA from "@/components/workspace/StrategyRoomCTA";
import { isSeedProfile, profileKind } from "@/lib/businessIntegrity";

export default function Business({ lang, t, data, setData, setSection }) {
  const b = data.business || {};
  const fa = lang === "fa";
  const kind = profileKind(b);
  const seeded = isSeedProfile(b) || b.sample_data;
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ business_name: b.business_name || "", industry: b.industry || "", description: b.description || "" });
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const r = await api.patch("/business", form); setData((d) => ({ ...d, business: r.data })); setEditing(false); toast.success(t.saved); }
    catch (err) { toast.error(errorText(err, "Could not save.")); } finally { setBusy(false); }
  };
  const clear = async () => { await api.delete("/business/sample-data"); setData((d) => ({ ...d, business: { ...d.business, metrics: {}, sample_data: false } })); };

  return (
    <div className="business-page">
      <div className="page-title"><p className="eyebrow">{t.eyebrows.business}</p><h2>{t.business}</h2><p>{kind === "customer" ? (b.description || t.missingProfileNote) : t.demoProfileNote}</p></div>
      <div className="strategy-invite"><div><h2>{t.startConversation}</h2><p>{t.startConversationHint}</p></div><StrategyRoomCTA t={t} prominent testId="business-strategy-room" /></div>
      {kind !== "customer" && <div className="integrity-banner" data-testid="business-demo-banner"><strong>{kind === "missing" ? t.missingProfile : t.demoProfile}</strong><p>{kind === "missing" ? t.missingProfileNote : t.demoProfileNote}</p></div>}
      <div className="business-grid">
        <div className="business-card">
          <p className="eyebrow">{fa ? "نمای کلی کسب‌وکار" : "Business overview"}</p>
          {editing ? (
            <form className="stack-form" onSubmit={save} data-testid="business-edit-form">
              <label>{t.businessName}<input required value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} data-testid="business-name-input" /></label>
              <label>{t.industry}<input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} data-testid="business-industry-input" /></label>
              <label>{t.description}<textarea rows="4" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="business-description-input" /></label>
              <div className="row-actions"><button className="primary-button" disabled={busy} data-testid="business-save-button">{t.save}</button><button type="button" className="quiet-button" onClick={() => setEditing(false)} data-testid="business-cancel-button">{t.cancel}</button></div>
            </form>
          ) : (
            <>
              <h3 data-testid="business-name">{b.business_name || t.missingProfile}</h3>
              <dl>
                <dt>{t.industry}</dt><dd>{b.industry ? (seeded ? `${b.industry} · ${t.demoProfile}` : b.industry) : t.noIndustry}</dd>
                <dt>{t.businessId}</dt><dd>{b.business_id || "—"}</dd>
                <dt>{t.dataStatus}</dt><dd data-testid="business-data-status">{kind === "demonstration" ? t.demoProfile : kind === "missing" ? t.missingProfile : t.customerProfile}</dd>
                <dt>{t.description}</dt><dd>{b.description ? (seeded ? `${t.storedText}: ${b.description}` : b.description) : t.missingProfileNote}</dd>
                <dt>{t.updated}</dt><dd>{b.updated_at ? new Date(b.updated_at).toLocaleDateString(fa ? "fa-IR" : "en-GB") : "—"}</dd>
              </dl>
              <button className="quiet-button" onClick={() => setEditing(true)} data-testid="business-edit-button">{kind === "customer" ? t.edit : t.confirmBusiness}</button>
            </>
          )}
        </div>
        <div className="business-card empty-card">
          <FolderOpen size={25} />
          <h3>{fa ? "هر فایل، عدد یا مشاهده‌ای که ثبت می‌کنید، حافظه نورین از کسب‌وکار شما را دقیق‌تر می‌کند." : "Every file, number or observation you record makes NOORIN's memory of your business sharper."}</h3>
          <div className="row-actions">
            <button className="primary-button" onClick={() => setSection("files")} data-testid="business-go-files-button">{t.files}</button>
            {b.sample_data && <button className="quiet-button" onClick={clear} data-testid="business-clear-data-button">{t.clear}</button>}
          </div>
        </div>
      </div>
    </div>
  );
}
